'use client';

/**
 * "Hear it before you make one." A visitor who lands on a page about making
 * something has to imagine the result; a tester said the landing read as work.
 * So the hero cover IS a play button: forty seconds of a narration our own
 * pipeline produced, in the visitor's language, with nothing to sign up for.
 *
 * The audio element is created on the first press, so the page costs nothing to
 * anyone who never listens.
 */

import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useTrack } from '@owlcat/app-shell';

export function HeroListen({
  src,
  playLabel,
  pauseLabel,
  children,
}: {
  src: string;
  playLabel: string;
  pauseLabel: string;
  /** The cover art (and its captions) the button sits on top of. */
  children: React.ReactNode;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  // The first step of the funnel: a visitor who HEARD a story before deciding.
  // Counted once per visit, so a pause-and-resume is not a second listener.
  const played = useRef(false);
  const locale = useLocale();
  const track = useTrack();

  useEffect(() => () => {
    audioRef.current?.pause();
    audioRef.current = null;
  }, []);

  const toggle = () => {
    let audio = audioRef.current;
    if (!audio) {
      audio = new Audio(src);
      audio.preload = 'none';
      audio.addEventListener('timeupdate', () => {
        if (audio!.duration > 0) setProgress(audio!.currentTime / audio!.duration);
      });
      audio.addEventListener('ended', () => {
        setPlaying(false);
        setProgress(0);
      });
      audioRef.current = audio;
    }
    if (audio.paused) {
      void audio.play().then(() => {
        setPlaying(true);
        if (!played.current) {
          played.current = true;
          track('landing_demo_played', { app_id: 'portal', surface: 'hero_audio', language: locale });
        }
      }).catch(() => setPlaying(false));
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="relative">
      {children}
      {/* Dim, the way a video thumbnail is dimmed — the white label has to read
          over any cover art. */}
      <div className="pointer-events-none absolute inset-0 bg-black/25" aria-hidden />

      {/* The whole cover is the button — on a phone there is no small target to
          find, and no text to read before understanding what happens. */}
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? pauseLabel : playLabel}
        /* The circle sits ABOVE the middle: the cover's lower half already carries
           the photo inset and the hero's name, and the two collided. */
        className="absolute inset-0 flex cursor-pointer flex-col items-center justify-start gap-3 pt-8 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/60 sm:pt-10"
        data-testid="hero-listen-button"
      >
        <span className="relative flex size-16 items-center justify-center rounded-full bg-primary shadow-2xl transition hover:scale-105 sm:size-20">
          {!playing && <span className="absolute inset-0 animate-ping rounded-full bg-primary/40" aria-hidden />}
          {playing ? (
            <Pause className="relative size-7 fill-white text-white sm:size-8" aria-hidden />
          ) : (
            /* optical centring — the triangle sits a hair right of centre */
            <Play className="relative size-7 translate-x-0.5 fill-white text-white sm:size-8" aria-hidden />
          )}
        </span>
        <span className="rounded-full bg-black/55 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur sm:text-sm">
          {playing ? pauseLabel : playLabel}
        </span>
      </button>

      {/* Progress: a thin line, not a control — this is a taste, not a player. */}
      {playing && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-black/20">
          <div
            className="h-full bg-primary transition-[width] duration-200"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
